"use client"

import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { cn } from "./lib/cn"

const GlassSelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({
  className,
  children,
  position = "popper",
  align = "start",
  side = "bottom",
  // Prefer opening below the trigger, but let Radix flip/shift when the
  // viewport has no room — otherwise a select near the bottom of the
  // screen renders off-screen with no way to reach the lower options.
  avoidCollisions = true,
  collisionPadding = 8,
  sideOffset = 4,
  ...props
}, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      data-slot="glass-select-content"
      position={position}
      align={align}
      side={side}
      avoidCollisions={avoidCollisions}
      collisionPadding={collisionPadding}
      sideOffset={sideOffset}
      className={cn(
        // Radix animation
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
        // Layout
        "relative z-50 min-w-32 overflow-x-hidden overflow-y-auto",
        "max-h-(--radix-select-content-available-height)",
        "origin-(--radix-select-content-transform-origin)",
        position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1",
        // Glass token
        "rounded-xl backdrop-blur-xl bg-white/96 border border-black/8 p-1",
        "shadow-[0_2px_8px_rgba(86,81,255,0.15),inset_0_1px_0_rgba(255,255,255,0.4)]",
        // Dark mode
        "dark:bg-white/12 dark:border-white/15",
        "dark:shadow-[0_2px_8px_rgba(86,81,255,0.2),inset_0_1px_0_rgba(255,255,255,0.08)]",
        // Text
        "text-popover-foreground",
        className
      )}
      {...props}
    >
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width) scroll-my-1"
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))

GlassSelectContent.displayName = "GlassSelectContent"

export { GlassSelectContent }
